import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import ProductCard from '@prisjakt/ui-product-card';
import ProductRow from '@prisjakt/ui-product-row';
import { bemGenerator } from '@prisjakt/ui-base';
import ListItem from '@prisjakt/ui-list-item';
import ProductImage from '@prisjakt/ui-product-image';

import * as S from './styles';

import './item.scss';

const styles = bemGenerator('pj-ui-search-item', [
  'outlink-cta',
  'outlink-icon',
]);

const SearchItem = ({
  item,
  onClick,
  onRawClick,
  type,
  mode,
  query,
  onSelect,
  styling,
  callToActionText,
}) => {
  const { t, linkTo } = useContext(AppContext);

  const qs = {
    q: query,
  };
  const externalLinkAttributes = {
    target: '_blank',
    rel: 'nofollow',
  };

  const onClickHandler = e => {
    if (onSelect) {
      e.preventDefault();
      onSelect(item);
    }
    onClick(type, item, e);

    if (onRawClick && type === 'raw') {
      onRawClick(item);
    }
  };

  const link = linkTo(type, item, qs);

  switch (type) {
    case 'product':
    case 'raw_sorted': {
      if (styling === 'simple') {
        return (
          <ListItem name={item.name} link={link} onClick={onClickHandler} />
        );
      }

      return mode === 'row' ? (
        <ProductRow
          onClick={onClickHandler}
          name={item.name}
          link={link}
          price={item.priceRaw ? item.priceRaw : null}
          subtitle={item.category && item.category.name}
          image={<ProductImage name={item.name} src={item.image} />}
          rating={item.rating}
          ratingCount={item.ratingCount}
          storesCount={item.priceCount}
          storesCountText={t('campaigns', 'item.sold-by-n-stores', {
            n: item.priceCount,
          })}
          showDivider
        />
      ) : (
        <ProductCard
          onClick={onClickHandler}
          name={item.name}
          link={link}
          price={item.price ? item.price : null}
          subtitle={item.category && item.category.name}
          image={<ProductImage name={item.name} src={item.image} />}
          rating={item.rating}
          ratingCount={item.ratingCount}
        />
      );
    }
    case 'raw':
      if (styling === 'simple') {
        return (
          <ListItem
            name={item.name}
            link={link}
            onClick={onClickHandler}
            linkAttributes={externalLinkAttributes}
          />
        );
      }

      return mode === 'row' ? (
        <ProductRow
          onClick={onClickHandler}
          name={item.name}
          price={item.price ? item.price : null}
          link={link}
          image={<ProductImage name={item.name} />}
          subtitle={<Outlink store={item.store} />}
          linkAttributes={externalLinkAttributes}
          showDivider
        />
      ) : (
        <ProductCard
          onClick={onClickHandler}
          name={item.name}
          price={item.price ? item.price : null}
          link={link}
          image={<ProductImage name={item.name} />}
          subtitle={<Outlink store={item.store} />}
          linkAttributes={externalLinkAttributes}
        />
      );

    case 'brand': {
      return (
        <ListItem
          name={item.name}
          link={link}
          onClick={onClickHandler}
          showArrow
        />
      );
    }
    case 'store': {
      return (
        <ListItem
          name={item.name}
          link={link}
          onClick={onClickHandler}
          showArrow
        />
      );
    }
    case 'brand_category':
    case 'brand_virtual_category':
    case 'virtual_category': // eslint-disable-line no-fallthrough
    case 'category': {
      return (
        <S.ListItem
          name={item.name}
          link={link}
          image={
            styling !== 'simple' && item.logo ? (
              <img height="28" width="28" src={item.logo} alt="" />
            ) : null
          }
          callToActionText={callToActionText}
          showArrow
          onClick={onClickHandler}
        />
      );
    }
    default:
      return null;
  }
};

const { object, func, oneOf, string } = PropTypes;

SearchItem.propTypes = {
  item: object.isRequired, // eslint-disable-line react/forbid-prop-types
  type: oneOf([
    'product',
    'raw',
    'raw_sorted',
    'category',
    'brand_category',
    'virtual_category',
    'brand_virtual_category',
    'store',
    'brand',
  ]).isRequired,
  styling: oneOf(['simple', 'rich']),
  mode: oneOf(['row', 'grid']),
  onRawClick: func, // Use for tracking
  onClick: func,
  onSelect: func,
  query: string.isRequired,
  callToActionText: string,
};

SearchItem.defaultProps = {
  mode: 'row',
  styling: 'rich',
  onRawClick: () => {},
  onClick: () => {},
  onSelect: undefined,
  callToActionText: undefined,
};

const Outlink = ({ store }) => {
  const { t } = useContext(AppContext);
  return (
    <span>
      {store}
      {' - '}
      <span className={styles['outlink-cta']}>
        {t('search', 'raw-search.go-to-shop')}
        <OpenInNewIcon size={14} className={styles['outlink-icon']} />
      </span>
    </span>
  );
};

Outlink.contextType = AppContext;

Outlink.propTypes = {
  store: string.isRequired,
};

export default memo(SearchItem);
